import { useEffect } from 'react';

export const useScrollDelegation = (
  containerRef: React.RefObject<HTMLElement>,
  isEnabled = true,
  summaryRef?: React.RefObject<HTMLElement>,
  anchorsRef?: React.RefObject<HTMLElement>
) => {
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const scroll = (el: HTMLElement, amount: number, event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const { scrollTop: st, scrollHeight: sh, clientHeight: ch } = el;
      el.scrollTop = Math.max(0, Math.min(st + amount, sh - ch));
    };

    const checkScrollable = (element: HTMLElement, tolerance: number) => {
      const rect = element.getBoundingClientRect();
      const { scrollTop, scrollHeight, clientHeight } = element;
      const isScrollable =
        scrollHeight > clientHeight &&
        ['auto', 'scroll'].includes(window.getComputedStyle(element).overflowY);

      return {
        rect,
        scrollTop,
        scrollHeight,
        clientHeight,
        isScrollable,
        inBounds: rect.top < window.innerHeight && rect.bottom > 0,
        atTop: scrollTop <= tolerance,
        atBottom: scrollTop >= scrollHeight - clientHeight - tolerance,
      };
    };

    const handleElementScroll = (
      element: HTMLElement,
      event: WheelEvent,
      deltaY: number,
      tolerance: number
    ) => {
      const scrollData = checkScrollable(element, tolerance);

      if (!scrollData.isScrollable) {
        return false;
      }

      const inElement =
        scrollData.inBounds &&
        event.target &&
        element.contains(event.target as Node);
      const canScroll =
        (deltaY > 0 && !scrollData.atBottom) ||
        (deltaY < 0 && !scrollData.atTop);

      if (inElement && canScroll) {
        scroll(element, deltaY * 0.5, event);
        return true;
      }

      if (inElement) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }

      return false;
    };

    const handleWheel = (event: WheelEvent) => {
      const container = containerRef.current;
      const summary = summaryRef?.current;
      const anchors = anchorsRef?.current;

      if (!container) {
        return;
      }

      const { deltaY } = event;
      const tolerance = 1;

      const windowScrollY = window.scrollY;
      const windowScrollHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const isWindowAtBottom =
        windowScrollY >= windowScrollHeight - windowHeight - tolerance;

      const footer = document.querySelector('footer');
      const isNearTop = windowScrollY <= tolerance * 3;
      const isFooterVisible =
        footer &&
        !isNearTop &&
        (() => {
          const footerRect = footer.getBoundingClientRect();
          return footerRect.top < window.innerHeight && footerRect.bottom > 0;
        })();

      if (anchors && handleElementScroll(anchors, event, deltaY, tolerance)) {
        return;
      }

      if (summary && handleElementScroll(summary, event, deltaY, tolerance)) {
        return;
      }

      if (isFooterVisible) {
        event.preventDefault();
        event.stopPropagation();

        const isWindowAtTop = windowScrollY <= tolerance;

        if (
          (deltaY > 0 && !isWindowAtBottom) ||
          (deltaY < 0 && !isWindowAtTop)
        ) {
          window.scrollBy(0, deltaY * 0.5);
        }
        return;
      }

      const containerData = checkScrollable(container, tolerance);
      const containerFullyVisible =
        containerData.rect.top >= 0 &&
        containerData.rect.bottom <= windowHeight;

      if (
        (deltaY > 0 && !containerData.atBottom && containerData.inBounds) ||
        (deltaY < 0 &&
          !isWindowAtBottom &&
          containerFullyVisible &&
          !containerData.atTop &&
          containerData.inBounds)
      ) {
        scroll(container, deltaY * 0.5, event);
      }
    };

    window.addEventListener('wheel', handleWheel, {
      passive: false,
      capture: true,
    });
    return () =>
      window.removeEventListener('wheel', handleWheel, { capture: true });
  }, [containerRef, isEnabled, summaryRef, anchorsRef]);
};
